import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Props = { companyId: string; isOwner: boolean };

const EVENTS: { key: string; label: string; hint: string }[] = [
  { key: "scheduled", label: "Service scheduled", hint: "Day-before heads-up with gate and pet reminders" },
  { key: "en_route", label: "Crew on the way", hint: "Sent when a crew taps “On the way”" },
  { key: "completed", label: "Job complete", hint: "Sent automatically when a stop is finished" },
  { key: "invoice", label: "Invoice / payment link", hint: "Sent with the Stripe payment link" },
];

export default function NotificationSettings({ companyId, isOwner }: Props) {
  const [enabled, setEnabled] = useState(true);
  const [events, setEvents] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("companies")
        .select("notify_sms_enabled, notify_events")
        .eq("id", companyId)
        .maybeSingle();
      if (data) {
        setEnabled(data.notify_sms_enabled);
        setEvents((data.notify_events ?? {}) as Record<string, boolean>);
      }
      setLoading(false);
    })();
  }, [companyId]);

  const save = async (patch: { notify_sms_enabled?: boolean; notify_events?: Record<string, boolean> }) => {
    const { error } = await supabase.from("companies").update(patch).eq("id", companyId);
    if (error) toast.error("Could not save notification settings");
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">Customer texts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="sms-master" className="text-sm font-medium">
            Automated SMS notifications
          </Label>
          <Switch
            id="sms-master"
            checked={enabled}
            disabled={!isOwner}
            onCheckedChange={(v) => {
              setEnabled(v);
              void save({ notify_sms_enabled: v });
            }}
          />
        </div>
        <div className="space-y-2 border-t border-border pt-3">
          {EVENTS.map((e) => (
            <div key={e.key} className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor={`sms-${e.key}`} className="text-sm">
                  {e.label}
                </Label>
                <p className="text-xs text-muted-foreground">{e.hint}</p>
              </div>
              <Switch
                id={`sms-${e.key}`}
                checked={events[e.key] !== false}
                disabled={!isOwner || !enabled}
                onCheckedChange={(v) => {
                  const next = { ...events, [e.key]: v };
                  setEvents(next);
                  void save({ notify_events: next });
                }}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
